import { useQuery } from '@tanstack/react-query'
import type { LinkProps } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import { type Meeting, MeetingVisibilityChip, meetingDetailQueryOptions } from '@/entities/meeting'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { DeleteMeetingDialog, EditMeetingDialog } from '@/features/edit-meeting'
import { useSearchStore } from '@/features/search'
import { ApiError } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { DateStamp } from '@/shared/ui/date-stamp'
import { FileRow } from '@/shared/ui/file-row'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { CommentThread } from '@/widgets/comment-thread'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'

export const MEETING_TABS = ['comments', 'docs'] as const
export type MeetingTab = (typeof MEETING_TABS)[number]
const TAB_LABELS: Record<MeetingTab, string> = { comments: 'Комментарии', docs: 'Документы' }

interface MeetingPageProps {
  meetingId: string
  activeTab: MeetingTab
  onTabChange: (tab: MeetingTab) => void
  // Хлебные крошки зависят от контура (проект/компания) — строит роут.
  renderBreadcrumb: (meeting: Meeting) => ReactNode
  // Ссылка на карточку документа встречи — роутинг остаётся у роута.
  docLink: (docId: string) => LinkProps
  /** Куда уйти после удаления встречи. */
  onDeleted: () => void
}

// Страница встречи — реюз в контуре проекта и в контуре компании. Заказчику
// открыта read-only: правка и удаление принадлежат сотруднику.
export function MeetingPageView({
  meetingId,
  activeTab,
  onTabChange,
  renderBreadcrumb,
  docLink,
  onDeleted,
}: MeetingPageProps) {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const meetingQ = useQuery(meetingDetailQueryOptions(meetingId))
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  if (meetingQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (meetingQ.error) {
    const e = meetingQ.error
    if (e instanceof ApiError && e.isForbidden) {
      return <StatusScreen title="Нет доступа" message="Встреча недоступна." />
    }
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Встреча не найдена" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить встречу" message="Обновите страницу." />
  }

  const m = meetingQ.data
  const hasBody = Boolean(m.notes || m.attachments.length)

  const tabs: EntityPageTab<MeetingTab>[] = MEETING_TABS.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'comments' ? (
        <CommentThread entityKind="meeting" entityId={m.id} />
      ) : (
        <DocumentSlots entityKind="meeting" entityId={m.id} docLink={docLink} />
      ),
  }))

  const header = (
    <EntityPageHeader
      breadcrumb={renderBreadcrumb(m)}
      title={m.title}
      meta={
        <>
          <Meta label="Когда">
            <DateStamp value={m.when} zone className="text-sm" />
          </Meta>
          {m.location && (
            <Meta label="Где">
              <span className="text-sm text-text-primary">{m.location}</span>
            </Meta>
          )}
          {m.participants && (
            <Meta label="Участники">
              <span className="text-sm text-text-primary">{m.participants}</span>
            </Meta>
          )}
          {m.url && (
            <Meta label="Ссылка">
              <a
                href={m.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                <Icon name="external-link" size={14} />
                Открыть встречу
              </a>
            </Meta>
          )}
          {m.created_by_name && (
            <Meta label="Завёл">
              <span className="text-sm text-text-secondary">{m.created_by_name}</span>
            </Meta>
          )}
          {isEmployee && (
            <Meta label="Заказчик">
              <MeetingVisibilityChip visible={m.customer_visible} />
            </Meta>
          )}
        </>
      }
      {...(isEmployee
        ? {
            tags: (
              <EntityTagsEditor
                entityKind="meeting"
                entityId={m.id}
                tags={m.tags ?? []}
                onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
              />
            ),
            actions: (
              <>
                <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                  <Icon name="pencil" size={16} />
                  Изменить
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteOpen(true)}>
                  <Icon name="close" size={16} />
                  Удалить
                </Button>
              </>
            ),
          }
        : {})}
      {...(hasBody
        ? {
            extra: (
              <div className="flex flex-col gap-3 rounded-card bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset">
                {m.notes && (
                  <p className="whitespace-pre-wrap text-sm text-text-secondary">{m.notes}</p>
                )}
                {m.attachments.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    {m.attachments.map((f) => (
                      <FileRow key={f.key} file={f} />
                    ))}
                  </div>
                )}
              </div>
            ),
          }
        : {})}
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabsLabel="Разделы встречи"
      />

      {editOpen && <EditMeetingDialog meeting={m} open onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeleteMeetingDialog
          meeting={{ id: m.id, title: m.title }}
          open
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </>
  )
}
